/* Offline attempt outbox (E7).
 *
 * A dependency-free IndexedDB store of drill attempts that failed to POST while
 * offline. `submitAttempt` (lib/api.ts) enqueues here on a transient failure and
 * `flushOutbox` drains it to /attempts/sync on reconnect. The sync endpoint is
 * idempotent on each attempt's ULID, so replaying an attempt that actually landed
 * is safe. Degrades to a no-op when `indexedDB` is unavailable (tests/SSR) so it
 * can never throw or break a render.
 */
import { trackWrite } from "./app-busy.js";
import type { AttemptSubmitBody } from "./api.js";

const DB_NAME = "bht-offline";
const STORE = "attempt-outbox";
const DB_VERSION = 1;

/** Dispatches "change" whenever the outbox size changes (drives the status pill). */
export const outboxEvents = new EventTarget();

function hasIDB(): boolean {
  // The ACCESS must be inside the try, not just the use. When a browser blocks
  // site data (Safari private browsing, third-party-blocked embeds), reading
  // `indexedDB` is a getter that throws SecurityError, and `typeof` only
  // suppresses ReferenceError for an unresolvable name — this one resolves, so
  // the guard was itself the throw site. Production saw exactly that on
  // /blog/ pages: "SecurityError: The operation is insecure." from our own
  // bundle, escaping the caller's try because hasIDB() runs before it.
  // flags.ts and theme.ts already do it this way for localStorage.
  try {
    return typeof indexedDB !== "undefined" && indexedDB !== null;
  } catch {
    return false;
  }
}

let dbPromise: Promise<IDBDatabase> | null = null;
function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  // The reset MUST happen after the assignment, not inside the executor. A
  // Promise executor runs synchronously during `new Promise(...)`, so an inner
  // `dbPromise = null` was immediately overwritten by the outer assignment of
  // the very promise that had just rejected — and every later call then got
  // that same rejected promise for the lifetime of the page, silently
  // discarding every subsequent offline answer. The async onerror path never
  // cleared it at all.
  const p = new Promise<IDBDatabase>((resolve, reject) => {
    // open() throws synchronously in the same blocked-storage contexts rather
    // than rejecting, so it needs its own guard even once hasIDB() is safe.
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (e) {
      reject(e instanceof Error ? e : new Error("indexedDB.open failed"));
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      // A cached connection that has been CLOSED is worse than no cache: every
      // later transaction throws "The database connection is closing" and the
      // outbox is dead for the life of the page. The browser closes it on its
      // own account (storage pressure, a service-worker update, another tab
      // upgrading the schema), and none of that goes through the open() error
      // path, so the p.catch() below never fires and the dead handle is kept.
      // Drop the cache the moment the connection signals it is going away.
      db.onclose = () => {
        if (dbPromise === p) dbPromise = null;
      };
      db.onversionchange = () => {
        if (dbPromise === p) dbPromise = null;
        db.close();
      };
      resolve(db);
    };
    req.onerror = () => reject(req.error ?? new Error("indexedDB.open errored"));
  });
  dbPromise = p;
  // Clear the cache on ANY failure, sync or async, so one transient open error
  // does not disable the outbox until the page is reloaded.
  p.catch(() => {
    if (dbPromise === p) dbPromise = null;
  });
  return p;
}

/**
 * Resolve on TRANSACTION COMMIT, not on request success.
 *
 * IndexedDB distinguishes the two, and a transaction can still abort after its
 * request reported success — on quota, on I/O failure, on the browser evicting
 * storage. Resolving at onsuccess meant enqueueAttempt announced a queued answer
 * that then rolled back and was never written.
 */
function request<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        // db.transaction() itself throws synchronously if the connection has
        // been closed or storage has failed, which is a separate failure from
        // the open() this already guards.
        let tx: IDBTransaction;
        try {
          tx = db.transaction(STORE, mode);
        } catch (e) {
          // This is the "connection is closing" case. Rejecting alone left the
          // dead connection cached, so every following call threw the same way
          // and the outbox stayed broken until a reload. Forget it here too:
          // onclose does not fire in every browser, and this path proves the
          // handle is unusable whether or not the event arrived.
          dbPromise = null;
          reject(e instanceof Error ? e : new Error("transaction failed"));
          return;
        }
        let value: T;
        let settled = false;
        const req = fn(tx.objectStore(STORE));
        req.onsuccess = () => {
          value = req.result;
        };
        req.onerror = () => {
          if (settled) return;
          settled = true;
          reject(req.error ?? new Error("request failed"));
        };
        // A write is only real once the transaction commits. A read can resolve
        // on complete too; both take the same path so there is one rule.
        tx.oncomplete = () => {
          if (settled) return;
          settled = true;
          resolve(value);
        };
        tx.onabort = () => {
          if (settled) return;
          settled = true;
          reject(tx.error ?? new Error("transaction aborted"));
        };
      }),
  );
}

export async function enqueueAttempt(body: AttemptSubmitBody): Promise<boolean> {
  if (!hasIDB()) return false;
  // Storage can be blocked at open() rather than at the property, so the probe
  // above passing is not a promise that the write will work. There is nowhere to
  // queue in that case, and a rejection here surfaces as an uncaught error on a
  // page the student is mid-way through, so degrade to the same no-op the
  // unavailable branch takes.
  try {
    await trackWrite(request("readwrite", (s) => s.put(body)));
  } catch {
    // Still a no-op rather than a rejection — an uncaught error mid-session
    // helps nobody — but the CALLER is now told, because submitAttempt was
    // reporting success for an answer that reached neither the server nor the
    // outbox, and the student had no way to know.
    return false;
  }
  outboxEvents.dispatchEvent(new Event("change"));
  return true;
}

export async function getQueuedAttempts(): Promise<AttemptSubmitBody[]> {
  if (!hasIDB()) return [];
  try {
    return (await request<AttemptSubmitBody[]>("readonly", (s) => s.getAll())) ?? [];
  } catch {
    return [];
  }
}

export async function removeQueued(ids: string[]): Promise<void> {
  if (!hasIDB() || ids.length === 0) return;
  // The whole operation, not just the open. db.transaction() can throw and the
  // transaction can abort, and both used to escape this "degrades to a no-op"
  // function as a rejection.
  try {
    const db = await openDb();
    await trackWrite(new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const s = tx.objectStore(STORE);
      for (const id of ids) s.delete(id);
      tx.oncomplete = () => resolve();
      tx.onabort = () => reject(tx.error ?? new Error("transaction aborted"));
      tx.onerror = () => reject(tx.error ?? new Error("delete failed"));
    }));
  } catch {
    return;
  }
  outboxEvents.dispatchEvent(new Event("change"));
}

export async function queuedCount(): Promise<number> {
  if (!hasIDB()) return 0;
  try {
    return (await request<number>("readonly", (s) => s.count())) ?? 0;
  } catch {
    return 0;
  }
}

/** Pure: split into chunks of at most `size` (kept small + exported for tests). */
export function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}
